<template>
    <div class="h-full bg-surface-background">
        <div class="flex flex-col" :class="{ 'max-h-180 min-h-svh min-[720px]:min-h-min': showTakeAction }">
            <Header class="shrink-0 grow" />
            <div class="w-full min-h-20 bg-error-500 flex items-center justify-center text-system-light" v-if="showTakeAction">
                <div class="max-w-[36rem] w-full p-4 flex flex-row justify-between gap-3 items-center">
                    <div class="flex flex-row gap-4 items-center">
                        <Icon type="alert" class="w-10" />
                        <div class="flex flex-col gap-1">
                            <div class="text-lg font-semibold">Violations found</div>
                            <div class="text-xs font-regular">Don't throw the label away</div>
                        </div>
                    </div>
                    <Button variant="light" label="Take action" size="md" class="shrink-0" @click="takeAction"></Button>
                </div>
            </div>
        </div>
        <div class="px-safe pb-safe grow">
            <div class="px-0 w-full max-w-[36rem] mx-auto flex flex-col gap-4 relative grow h-full">
                <div v-if="!hasData" class="grow h-full flex flex-col items-center justify-center p-6 py-12 gap-4">
                    <div class="flex flex-col gap-4 justify-center items-center bg-neutral-100 rounded-full aspect-square w-16">
                        <Icon type="label" class="w-8 text-sub" :filled="true" />
                    </div>
                    <div class="flex flex-col justify-center items-center gap-0.5">
                        <div class="font-medium text-sub text-lg">It's empty here</div>
                        <div class="font-medium text-sm text-inactive">No data available</div>
                    </div>
                </div>
                <template v-else>
                    <DataTabs>
                        <template #temperature>
                            <div class="p-4 flex flex-col gap-4">
                                <div class="flex flex-row gap-4">
                                    <TimeZoneSelect v-model="interfaceStore.preferences.timeZone" size="md" class="max-w-[100%] w-[60%] grow" locale="en" />

                                    <Switch
                                        size="sm"
                                        v-model="interfaceStore.preferences.fahrenheit"
                                        :options="[
                                            {
                                                icon: 'celsius',
                                                iconFilled: true
                                            },
                                            {
                                                icon: 'fahrenheit',
                                                iconFilled: true
                                            }
                                        ]"
                                    />
                                </div>

                                <Card v-if="labelStore.labelData?.type === LabelType.TempSense || labelStore.labelData?.type === LabelType.HumiditySense" class="[&&]:gap-1 grow-0">
                                    <template #header>
                                        <div class="flex flex-row gap-2 items-center">
                                            <UnitBadge :units="[UnitTypes.Temperature]" size="md" />
                                            <h2>Temperature logs</h2>
                                        </div>
                                    </template>
                                    <template #chart>
                                        <LineChart
                                            unitType="temperature"
                                            :datapoints="selectedDatapoints ?? []"
                                            :alertLevel="alertLevel"
                                            :limits="{
                                                min: labelStore.labelData?.senseConfig?.limits?.minTemperature,
                                                max: labelStore.labelData?.senseConfig?.limits?.maxTemperature
                                            }"
                                            :options="{
                                                timeZone: interfaceStore.preferences.timeZone,
                                                temperatureUnit: interfaceStore.preferences.fahrenheit ? 'fahrenheit' : 'celsius'
                                            }"
                                        />
                                        <div class="pt-6 px-3 md:px-5 pb-4">
                                            <RangeSlider
                                                :modelValue="timelineRange"
                                                :minValue="startTime"
                                                :maxValue="endTime"
                                                :options="{ unitType: 'timestamp' }"
                                                variant="neutral"
                                            />
                                        </div>
                                    </template>
                                </Card>

                                <div class="flex max-[350px]:flex-col flex-row gap-3">
                                    <!-- Temperature limits -->
                                    <Card class="min-w-0 @container">
                                        <div
                                            class="flex flex-col gap-4 h-full"
                                            :class="[
                                                (!labelStore.labelIsConfigured ||
                                                    !labelStore.labelInDatabase ||
                                                    (!labelStore.labelData?.senseConfig?.limits?.maxTemperature && !labelStore.labelData?.senseConfig?.limits?.minTemperature)) &&
                                                    'text-inactive'
                                            ]"
                                        >
                                            <h2>Temperature limits</h2>
                                            <Limits :minLimit="temperatureValues.minLimit" :maxLimit="temperatureValues.maxLimit" suffix="°" />
                                        </div>
                                    </Card>

                                    <!-- Current temperature -->
                                    <Card v-if="temperatureValues.snapshot && isLabelTap" class="min-w-0">
                                        <div class="flex flex-col gap-4 w-full">
                                            <h2>Current temperature</h2>
                                            <div class="w-full flex justify-center items-center">
                                                <div class="-mr-2 -ml-2">
                                                    <GaugeChart
                                                        :value="temperatureValues.snapshot"
                                                        :alertLevel="temperatureSnapshotAlertLevel"
                                                        :min="temperatureSnapshotMinMax.min"
                                                        :max="temperatureSnapshotMinMax.max"
                                                        :options="{}"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>

                                <!-- Detailed temperature values -->
                                <div class="grid grid-cols-2 xs:grid-cols-3 gap-x-3 gap-y-2 mb-6" v-if="labelInNewDatabase">
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Average temperature">{{ temperatureValues.average }}</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Minimum temperature">{{ temperatureValues.min }}</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Maximum temperature">{{ temperatureValues.max }}</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Degree hours">{{ formatNumber(calculatedSenseData?.analysedValues.temperature.degreeHour, 1) }}</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Mean kinetic temperature">{{ temperatureValues.meanKinetic }}</DetailListItem>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #humidity v-if="labelStore.labelData?.type === LabelType.HumiditySense">
                            <div class="p-4 flex flex-col gap-4">
                                <div class="flex flex-row gap-4">
                                    <TimeZoneSelect v-model="interfaceStore.preferences.timeZone" size="md" class="max-w-[100%] w-[60%] grow" locale="en" />
                                </div>

                                <Card v-if="labelStore.labelData?.type === LabelType.HumiditySense" class="[&&]:gap-1 grow-0">
                                    <template #header>
                                        <div class="flex flex-row gap-2 items-center">
                                            <UnitBadge :units="[UnitTypes.Humidity]" size="md" />
                                            <h2>Humidity logs</h2>
                                        </div>
                                    </template>
                                    <template #chart>
                                        <LineChart
                                            unitType="humidity"
                                            :datapoints="selectedDatapoints ?? []"
                                            :alertLevel="alertLevel"
                                            :limits="{
                                                min: labelStore.labelData?.senseConfig?.limits?.minHumidity,
                                                max: labelStore.labelData?.senseConfig?.limits?.maxHumidity
                                            }"
                                            :options="{
                                                timeZone: interfaceStore.preferences.timeZone
                                            }"
                                        />
                                        <div class="pt-6 px-3 md:px-5 pb-4">
                                            <RangeSlider
                                                :modelValue="timelineRange"
                                                :minValue="startTime"
                                                :maxValue="endTime"
                                                :options="{ unitType: 'timestamp' }"
                                                variant="neutral"
                                            />
                                        </div>
                                    </template>
                                </Card>

                                <div class="flex max-[350px]:flex-col flex-row gap-4">
                                    <!-- Humidity limits -->
                                    <Card class="min-w-0">
                                        <div
                                            class="flex flex-col gap-4"
                                            :class="[
                                                (!labelStore.labelIsConfigured ||
                                                    !labelStore.labelInDatabase ||
                                                    (!labelStore.labelData?.senseConfig?.limits?.maxHumidity && !labelStore.labelData?.senseConfig?.limits?.minHumidity)) &&
                                                    'text-inactive'
                                            ]"
                                        >
                                            <h2>Humidity limits</h2>

                                            <Limits :minLimit="humidityValues.minLimit" :maxLimit="humidityValues.maxLimit" suffix="%" />
                                        </div>
                                    </Card>
                                </div>

                                <!-- Detailed humidity values -->
                                <div class="grid grid-cols-2 xs:grid-cols-3 gap-x-3 gap-y-2 mb-6" v-if="labelInNewDatabase">
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Average humidity">{{ humidityValues.average }}%</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Minimum humidity">{{ humidityValues.min }}%</DetailListItem>
                                    </div>
                                    <div class="bg-system-light rounded-2xl shadow-sm py-2 px-4 md:px-6 md:py-3">
                                        <DetailListItem label="Maximum humidity">{{ humidityValues.max }}%</DetailListItem>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DataTabs>
                </template>
            </div>
        </div>

        <!-- Light section -->
        <div class="w-full bg-system-light">
            <div class="px-4 py-6 w-full max-w-[36rem] mx-auto flex flex-col gap-4 relative">
                <div class="flex flex-col gap-4 pb-6">
                    <template v-if="labelStore.labelIsConfigured">
                        <h2>Time</h2>
                        <div class="grid grid-cols-2 gap-4 gap-y-4">
                            <DetailListItem label="Start time" :value="timestampToDate(startTime)" />
                            <DetailListItem label="Elapsed time" :value="toDaysHoursMinutesSeconds(elapsedTime)" />
                            <DetailListItem v-if="elapsedTime > 0" label="End time" :value="timestampToDate(endTime)" />
                            <DetailListItem label="Interval" :value="toDaysHoursMinutesSeconds(labelStore.labelData?.senseConfig?.interval)" />
                        </div>

                        <!-- Divider-->
                        <hr class="border-0 border-b-4 border-b-surface-background rounded-full" />
                    </template>

                    <h2>Details</h2>
                    <div class="grid grid-cols-2 gap-4 gap-y-4">
                        <template v-if="labelStore.labelIsConfigured">
                            <DetailListItem label="Available datapoints" :value="formatNumber(labelStore.labelData?.calculatedSenseData?.numberOfMeasurementsOccurred)" />
                        </template>
                        <DetailListItem label="Label type">{{ labelType }}</DetailListItem>
                        <DetailListItem label="Reference" :copyValue="labelStore.labelData?.labelReference" v-if="labelStore.labelData?.labelReference">
                            {{ labelStore.labelData?.labelReference }}
                        </DetailListItem>
                        <DetailListItem label="Reference" class="text-inactive" v-else>Not set</DetailListItem>

                        <DetailListItem v-if="labelStore.labelData?.label?.sequenceCode" label="Label code" :copyValue="labelStore.labelData?.label?.sequenceCode">
                            {{ labelStore.labelData?.label?.sequenceCode }}
                        </DetailListItem>
                        <DetailListItem label="Label id" :copyValue="labelId" :value="labelId" />
                        <DetailListItem v-if="reportId" label="Report id" :copyValue="reportId" :value="reportId" />

                        <DetailListItem label="Activated at" v-if="labelStore.labelData?.senseConfig?.configTime">
                            {{ timestampToDate(labelStore.labelData?.senseConfig?.configTime) }}
                        </DetailListItem>
                    </div>

                    <template v-if="isLabelTap">
                        <!-- Divider-->
                        <hr class="border-0 border-b-4 border-b-surface-background rounded-full" />

                        <h2>Status</h2>
                        <div class="grid grid-cols-2 gap-4 gap-y-4">
                            <DetailListItem label="Label">
                                <div class="flex flex-row gap-1"><StatusIndicator /> {{ labelStore.statusStringShort }}</div>
                            </DetailListItem>

                            <!-- TODO: Add battery status when BMS is enabled -->
                            <!-- <DetailListItem
                                v-if="labelStore.labelData?.calculatedSenseData?.batteryStatus"
                                label="Battery (beta)"
                                :value="`${labelStore.labelData?.calculatedSenseData?.batteryStatus}%`"
                            />
                            <DetailListItem v-else label="Battery" :value="labelStore.batteryStatusString" /> -->
                            <DetailListItem label="Battery" :value="labelStore.batteryStatusString" />
                        </div>
                    </template>
                </div>
            </div>

            <!-- Action card or white space for footer -->
            <div ref="actionCard" v-if="showTakeAction">
                <TakeAction :class="{ 'pb-24': labelReportCanBeShared }" />
            </div>
            <div v-else :class="{ 'pb-24': labelReportCanBeShared }"></div>

            <!-- Footer logo -->
            <!-- <div class="px-4 pb-8 w-full max-w-[36rem] mx-auto flex flex-col gap-4 relative items-center justify-center">
                <FooterLogo />
            </div> -->

            <!-- Bottom drawer -->
            <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="transform translate-y-full"
                enter-to-class="transform translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="transform translate-y-0"
                leave-to-class="transform translate-y-full"
            >
                <div class="w-full bg-system-light shadow-up fixed bottom-0 left-0 right-0 z-10" v-if="labelReportCanBeShared || labelReportCanBeDownloaded" v-show="showFooter">
                    <div class="px-4 py-6 w-full max-w-[36rem] mx-auto flex flex-row gap-2 relative">
                        <Button
                            v-if="labelReportCanBeDownloaded"
                            label="Download"
                            size="lg"
                            variant="primary"
                            icon="download"
                            icon-position="left"
                            @click="showDownloadModal = true"
                            class="w-0 grow-5"
                        />
                        <Button
                            v-if="labelReportCanBeShared"
                            label="Share"
                            size="lg"
                            variant="blue"
                            icon="share"
                            icon-position="left"
                            @click="shareUrl"
                            class="w-0 grow-5 shrink-0"
                        />
                    </div>
                </div>
            </Transition>
        </div>
        <Download :show="showDownloadModal" :labelReport="labelStore.labelData" :id="reportId" @close="showDownloadModal = false" />
    </div>
</template>

<script setup lang="ts" async>
    import { computed, ref, watch, provide, useTemplateRef, onMounted, onBeforeUnmount } from 'vue'
    import { transformCelsiusToFahrenheit, parseTemperature } from '@/utils/temperature'
    import { toDaysHoursMinutesSeconds, timestampToDate, UTCTimeToDate } from '@/utils/time'
    import { formatNumber } from '@/utils/number'
    import { useRoute, useRouter } from 'vue-router'
    import { UnitTypes, LabelType } from '@/types/enums'
    import { useLabelStore } from '@/stores/label'
    import { useInterfaceStore } from '@/stores/interface'
    import { useCopy, useLabelReport, useThemeColor, useError } from '@/composables'

    import { Icon, Button, UnitBadge, LineChart, Switch, TimeZoneSelect, DataTabs, GaugeChart, RangeSlider } from '@tapp.online/ui'
    import theme from '@tapp.online/ui/tailwindcss/theme'
    import Header from '@/components/layout/Header.vue'
    import Card from '@/components/layout/Card.vue'
    import TakeAction from '@/components/layout/TakeAction.vue'
    import DetailListItem from '@/components/elements/DetailListItem.vue'
    import Note from '@/components/elements/Note.vue'
    import StatusIndicator from '@/components/elements/StatusIndicator.vue'
    import FooterLogo from '@/components/elements/FooterLogo.vue'
    import Limits from '@/components/elements/Limits.vue'
    import Download from '@/components/layout/Download.vue'

    const route = useRoute()
    const router = useRouter()
    const labelStore = useLabelStore()
    const interfaceStore = useInterfaceStore()
    const { updateThemeColor } = useThemeColor()
    const { copyValue } = useCopy()
    const { createQuickTap, parseQuickTapData, getLabelReport } = useLabelReport()
    const { logError } = useError()

    const actionCard = useTemplateRef('actionCard')

    const showTakeAction = computed(() => alertLevel.value > 1 && isLabelTap.value)
    const alertLevel = computed(() => {
        if (labelStore.labelData?.alert) return labelStore.labelData?.alert?.level
        return labelStore.labelHasViolations ? 1 : 0
    })
    const alertLevelThemeColors = computed(() => {
        const colors = {
            '-1': {
                light: theme['color-neutral-100'],
                dark: theme['color-neutral-100']
            },
            0: {
                light: theme['color-success-200'],
                dark: theme['color-success-500']
            },
            1: {
                light: theme['color-warning-200'],
                dark: theme['color-warning-500']
            },
            2: {
                light: theme['color-error-200'],
                dark: theme['color-error-500']
            }
        }
        return colors[alertLevel.value as keyof typeof colors]
    })
    const hasData = computed(() => {
        return labelStore.labelData?.calculatedSenseData?.datapoints?.length > 0
    })
    const labelInNewDatabase = computed(() => labelStore.labelHasReadAccessToken)
    const reportId = computed(() => labelStore.labelData?.id)
    const labelId = computed(() => labelStore.labelData?.label?.uid)

    const calculatedSenseData = computed(() => labelStore.labelData?.calculatedSenseData)
    const senseConfig = computed(() => labelStore.labelData?.senseConfig)

    const labelType = computed(() => {
        const labelTypes = {
            tempSense: 'Temperature',
            humiditySense: 'Humidity'
        }
        return labelTypes[labelStore.labelData?.type as keyof typeof labelTypes]
    })
    const startTime = computed(() => calculatedSenseData.value?.startTime ?? senseConfig.value?.configTime + senseConfig.value?.startDelay)
    const endTime = computed(() => calculatedSenseData.value?.currentTime ?? 0)
    const elapsedTime = computed(() => calculatedSenseData.value?.preciseElapsedTime)
    const timelineRange = ref<[number, number]>([0, 0])
    const selectedStartTime = computed<number>(() => timelineRange.value[0])
    const selectedEndTime = computed<number>(() => timelineRange.value[1])
    const selectedDatapoints = computed(() => {
        return calculatedSenseData.value?.datapoints.filter((datapoint: any) => datapoint.timestamp >= selectedStartTime.value && datapoint.timestamp <= selectedEndTime.value)
    })
    watch(
        [startTime, endTime],
        () => {
            timelineRange.value = [startTime.value ?? 0, endTime.value ?? 0]
        },
        { immediate: true }
    )

    const temperatureUnit = computed(() => (interfaceStore.preferences.fahrenheit ? 'fahrenheit' : 'celsius'))
    const temperatureValues = computed(() => {
        return {
            snapshot: parseTemperature(calculatedSenseData?.value?.measurementSnapshot?.temperature, { temperatureUnit: temperatureUnit.value, includeUnit: false }) as number,
            average: parseTemperature(calculatedSenseData?.value?.analysedValues?.temperature?.average, { temperatureUnit: temperatureUnit.value, includeUnit: true }) as number,
            min: parseTemperature(calculatedSenseData?.value?.attainedValues?.minTemperature, { temperatureUnit: temperatureUnit.value, includeUnit: true }) as string,
            max: parseTemperature(calculatedSenseData?.value?.attainedValues?.maxTemperature, { temperatureUnit: temperatureUnit.value, includeUnit: true }) as string,
            minLimit: parseTemperature(senseConfig?.value?.limits?.minTemperature, { temperatureUnit: temperatureUnit.value, includeUnit: false }) as number,
            maxLimit: parseTemperature(senseConfig?.value?.limits?.maxTemperature, { temperatureUnit: temperatureUnit.value, includeUnit: false }) as number,
            meanKinetic: parseTemperature(calculatedSenseData?.value?.analysedValues?.temperature?.meanKineticTemperature, {
                temperatureUnit: temperatureUnit.value,
                includeUnit: true
            }) as string
        }
    })
    const temperatureSnapshotAlertLevel = computed(() => {
        const minLimit = senseConfig?.value?.limits?.minTemperature
        const maxLimit = senseConfig?.value?.limits?.maxTemperature
        const currentTemperature = calculatedSenseData?.value?.measurementSnapshot?.temperature
        const offset = 2
        if (maxLimit && currentTemperature > maxLimit) {
            if (currentTemperature > maxLimit + offset) return 2
            else return 1
        }
        if (minLimit && currentTemperature < minLimit) {
            if (currentTemperature < minLimit - offset) return 2
            else return 1
        }
        return 0
    })
    const temperatureSnapshotMinMax = computed(() => {
        return {
            min: parseTemperature(-10, { temperatureUnit: temperatureUnit.value, includeUnit: false }) as number,
            max: parseTemperature(50, { temperatureUnit: temperatureUnit.value, includeUnit: false }) as number
        }
    })

    const humidityValues = computed(() => {
        return {
            average: formatNumber(calculatedSenseData?.value?.analysedValues?.humidity?.average, 1),
            min: formatNumber(calculatedSenseData?.value?.attainedValues?.minHumidity),
            max: formatNumber(calculatedSenseData?.value?.attainedValues?.maxHumidity),
            minLimit: senseConfig?.value?.limits?.minHumidity,
            maxLimit: senseConfig?.value?.limits?.maxHumidity
        }
    })

    const isLabelTap = computed(() => labelStore.isLabelTap)

    /**
     * A new QuickTap
     * @description Handles the url when the label data is from a new QuickTap
     * @param {string} route.fullPath - The full path of the current route
     */

    const handleSenseDataUrl = async () => {
        labelStore.isLabelTap = true
        labelStore.senseDataUrl = route.fullPath
        try {
            let createdNewTap = false
            let labelReportToken

            // Attempt to create new quick tap
            try {
                const { labelReport, tokens } = await createQuickTap(route.fullPath)
                if (labelReport) {
                    labelStore.labelData = labelReport
                    labelStore.labelInDatabase = true
                    createdNewTap = true
                    labelReportToken = tokens?.labelReportToken

                    logToAxiom({ url: route.fullPath, data: labelReport, uid: labelReport.label.uid })
                }
            } catch (error: any) {
                if (error instanceof Error) console.log(`Something went wrong: ${error.message || error}`)
                else console.warn(error.message || 'Something went wrong')
            }

            // If created new quick tap fails parse the quick tap data directly
            if (!createdNewTap) {
                labelStore.labelInDatabase = false
                logError({ reason: 'Failed to create label tap', level: 'warning' })

                try {
                    const { data } = await parseQuickTapData(route.fullPath)
                    logToAxiom({ url: route.fullPath, ...data, uid: data.label.uid })
                    labelStore.labelData = data
                } catch (error: any) {
                    console.warn(error.message || 'Something went wrong')
                    // Instead of throwing in finally, throw here
                    throw new Error('Failed to parse quick tap data')
                }

                // Clean up url if not in test mode
                if (route.query.isTestData !== 'true') router.replace({ query: {} })
            } else {
                // Update url with token
                if (labelReportToken) {
                    router.replace({ query: { token: labelReportToken, title: `Tapp label report for ${labelStore.labelData?.label?.uid}` } })
                } else {
                    // No token found for some reason
                    // Clean up url if not in test mode
                    if (route.query.isTestData !== 'true') router.replace({ query: {} })
                }
            }
        } catch (error) {
            router.push({ name: 'error', params: { type: 'data' } })
        }
    }

    const logToAxiom = async (payload: {}) => {
        if (import.meta.env.DEV) return console.warn('Axiom logging is disabled in development mode')
        try {
            fetch('/.netlify/functions/logToAxiom', {
                method: 'POST',
                body: JSON.stringify(payload)
            })
        } catch (error) {
            console.log(error)
        }
    }

    /**
     * An exisiting label report
     * @description Handles the url when the label data is from an existing label report
     * @param {string} route.fullPath - The full path of the current route
     */

    const handleTokenUrl = async (token: string) => {
        try {
            const labelReportData = await getLabelReport(token)

            if (!labelReportData) throw 'Label data not found'

            if (labelReportData) {
                labelStore.labelData = labelReportData
                labelStore.labelInDatabase = true

                // Add share title to url
                if (labelStore.labelData?.label?.uid) router.replace({ path: `/${token}`, query: { title: `Tapp label report for ${labelStore.labelData?.label?.uid}` } })
            }
        } catch (error: any) {
            router.push({ name: 'error', params: { type: 'token' } })

            if (error instanceof Error) return console.log(`Something went wrong: ${error.message || error}`)
            else console.warn(error.message || 'Something went wrong')
        }
    }

    /*
     * Handle url based on meta type
     */
    if (route.meta.type === 'sd') await handleSenseDataUrl()
    else if (route.meta.type === 'token' && route.params.token) await handleTokenUrl(route.params.token as string)
    else if (route.meta.type === 'token') await handleTokenUrl(route.query?.token as string)
    else router.push({ name: 'error', params: { type: 'token' } })

    /**
     * Handle footer visibility
     */
    const scrollThreshold = 150
    const showFooter = ref<boolean>(false)

    const handleScroll = (): void => {
        const scrollPosition = window.scrollY || document.documentElement.scrollTop
        showFooter.value = scrollPosition > scrollThreshold

        // if (scrollPosition > 0) updateThemeColor(alertLevelThemeColors.value.dark)
        // else updateThemeColor(alertLevelThemeColors.value.light)
    }
    onMounted((): void => {
        window.addEventListener('scroll', handleScroll)
        // Initial check in case page is loaded scrolled down
        handleScroll()
        updateThemeColor(alertLevelThemeColors.value.light)
    })
    onBeforeUnmount((): void => {
        window.removeEventListener('scroll', handleScroll)
    })

    /**
     * Sharing label report
     */
    const labelReportCanBeShared = computed(() => (!!route?.query?.token || !!route?.params?.token) && labelStore.labelHasReadAccessToken)

    const shareUrl = async () => {
        const token = route?.query?.token || route?.params?.token
        const shareUrl = `https://tapp.report/${token}`

        // Check if it's a mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

        try {
            if (isMobile && navigator.share) {
                // Only works on secure https connections
                await navigator
                    .share({
                        url: shareUrl,
                        title: 'Tapp label report'
                    })
                    .catch((error) => {
                        console.warn('Share was canceled or failed:', error)
                    })
            } else {
                copyValue(shareUrl)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const takeAction = () => {
        actionCard.value?.scrollIntoView({ behavior: 'smooth' })
    }

    /**
     * Downloading label report
     */
    const labelReportCanBeDownloaded = computed(() => hasData.value && labelStore.labelHasReadAccessToken)
    const showDownloadModal = ref(false)
</script>
